import * as React from 'react'
import { FunctionComponent, useState, useEffect } from 'react'
import { useMediaQuery } from 'react-responsive'
import modifyScheduleData, {
  handleSkipSession,
  handleSaveSessionDate
} from '@builder/alphas/schedule/alpha-schedule.utils'
import {
  SaveScheduleContext,
  ResourceClickHandlerContext
} from '@react-ui/context'
import { SessionCardListDraggable, SessionCardListMobile } from '@react-ui'
import { useSessions } from '../../hooks'
import OnboardingCard from '@builder/react-ui/scheduler/onboarding-card/onboarding-card'

interface SessionCardListProps {
  alpha
  saveSchedule
  planning
  resourceClickHandler: (resource) => {}
  defaultCoursePlan
  userCanModify?: boolean
  isCinderellaEnabled?: boolean
  moveItEnabled?: boolean
  date?
  dateUpdated?
}
const SessionCardList: FunctionComponent<SessionCardListProps> = (props) => {
  const {
    alpha,
    saveSchedule,
    planning,
    resourceClickHandler,
    defaultCoursePlan,
    userCanModify = true,
    isCinderellaEnabled = false,
    moveItEnabled = false
  } = props

  const { sessions, setAllSessions, setTrainingSessions, setCourseSessions } =
    useSessions(
      alpha.planning && alpha.date
        ? modifyScheduleData(alpha.planning, defaultCoursePlan, alpha.date)
        : null
    )

  const isMobile = useMediaQuery({ maxWidth: '1110px' })

  // Detect changes in `planning` or relevant props and update `sessions`
  useEffect(() => {
    if (alpha.planning && alpha.date) {
      const updatedSessions = modifyScheduleData(
        alpha.planning,
        defaultCoursePlan,
        alpha.date
      )
      setAllSessions(updatedSessions)
    }
  }, [alpha.date])

  function skipSession(skippedItemId: number, category: string) {
    handleSkipSession(
      skippedItemId,
      category,
      alpha,
      defaultCoursePlan,
      saveSchedule,
      setAllSessions
    )
  }

  function saveSessionDate(id, newDate, category: string) {
    handleSaveSessionDate(
      id,
      newDate,
      category,
      alpha,
      defaultCoursePlan,
      saveSchedule,
      setAllSessions
    )
  }

  return (
    <div
      className={`cursor-default ${
        !isCinderellaEnabled
          ? 'flex flex-col bg-white border border-solid border-alpha-oatmeal-10 rounded-lg flex-1 w-auto box-border mt-[1rem] mr-[2rem] mb-[1rem] ml-[2rem] pt-[2rem] pr-[3rem] pb-[2rem] pl-[3rem]'
          : ''
      }`}
    >
      <div className='flex flex-col w-full lg:flex-row gap-4 lg:gap-8'>
        <OnboardingCard
          cardDescription={$localize`:onboarding.description1|onboarding description1 - onboarding description 1@@onboardingDescription1:Designing a Successful Alpha Course`}
          cardTitle={$localize`:onboarding.title1|onboarding title1 - onboarding title 1@@onboardingTitle1:Onboarding Tutorial 1`}
          image='../images/cards/onboarding_card1.png'
        />
        <OnboardingCard
          cardDescription={$localize`:onboarding.description2|onboarding description2 - onboarding description 2@@onboardingDescription2:How to Set Up My Alpha Schedule?`}
          cardTitle={$localize`:onboarding.title2|onboarding title2 - onboarding title 2@@onboardingTitle2:Onboarding Tutorial 2`}
          image='../images/cards/onboarding_card2.png'
          schedulerVersion={true}
        />
      </div>
      <ResourceClickHandlerContext.Provider value={{ resourceClickHandler }}>
        <SaveScheduleContext.Provider
          value={{
            userCanModify,
            alpha,
            saveSessionDate,
            skipSession,
            sessions,
            setTrainingSessions,
            setCourseSessions,
            isCinderellaEnabled
          }}
        >
          {sessions?.training.length > 0 && (
            <>
              <h2
                data-qa-name='training-heading'
                className='text-2xl mt-[5.50rem]'
              >{$localize`:schedule.heading|Schedule Heading - My Team Training Schedule@@trainingScheduleHeading:My Team Training Schedule`}</h2>

              {isMobile ? (
                <SessionCardListMobile sessions={sessions.training} />
              ) : (
                <SessionCardListDraggable isTraining={true} />
              )}
            </>
          )}

          {sessions?.course.length > 0 && (
            <>
              <h2
                data-qa-name='schedule-heading'
                className='text-2xl mt-[5.50rem]'
              >{$localize`:schedule.heading|Schedule Heading - My Alpha Schedule@@myAlphaScheduleHeading:My Alpha Schedule`}</h2>

              {isMobile ? (
                <SessionCardListMobile
                  sessions={sessions.course}
                  totalWeekendSessions={sessions.totalWeekendSessions}
                />
              ) : (
                <SessionCardListDraggable
                  isTraining={false}
                  weekendSessions={sessions.totalWeekendSessions}
                />
              )}
            </>
          )}
        </SaveScheduleContext.Provider>
      </ResourceClickHandlerContext.Provider>
    </div>
  )
}

export default SessionCardList
